import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const AGENCY_JWT_KEY = 'agency_jwt';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); }`;

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #08080f 0%, #0d0d1c 60%, #111128 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  animation: ${fadeIn} 250ms ease;
`;

const Card = styled.div`
  background: linear-gradient(160deg, #111120 0%, #16162a 100%);
  border: 1px solid rgba(255,255,255,0.09);
  border-radius: 22px;
  padding: 48px 40px 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.6);
  animation: ${slideUp} 250ms ease;
`;

const Logo = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: #ff4757;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 32px;
  text-align: center;
`;

const Title = styled.h1`
  color: white;
  font-size: 1.45rem;
  font-weight: 700;
  margin: 0 0 8px;
  text-align: center;
`;

const Sub = styled.p`
  color: rgba(255,255,255,0.38);
  font-size: 0.85rem;
  text-align: center;
  margin: 0 0 32px;
`;

const Field = styled.div`margin-bottom: 16px;`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  background: #10101c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: white;
  font-size: 0.92rem;
  padding: 12px 14px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const LoginBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.2s, transform 0.15s;
  &:hover:not(:disabled) { background: #e0404f; transform: translateY(-1px); }
  &:disabled { opacity: 0.55; cursor: default; }
`;

const ErrorMsg = styled.p`
  color: #ff6b7a;
  font-size: 0.82rem;
  text-align: center;
  margin: 12px 0 0;
`;

const ForgotNote = styled.p`
  color: rgba(255,255,255,0.25);
  font-size: 0.78rem;
  text-align: center;
  margin: 20px 0 0;
  line-height: 1.5;
`;

const AgencyLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/agency/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      localStorage.setItem(AGENCY_JWT_KEY, data.token);
      navigate('/agency/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Card>
        <Logo>vusub.ai</Logo>
        <Title>Agency Portal</Title>
        <Sub>Log in with your agency credentials.</Sub>
        <form onSubmit={handleSubmit} noValidate>
          <Field>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@agency.com"
              required
              autoFocus
            />
          </Field>
          <Field>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </Field>
          <LoginBtn type="submit" disabled={loading || !email || !password}>
            {loading ? 'Logging in…' : 'Log in'}
          </LoginBtn>
          {error && <ErrorMsg>{error}</ErrorMsg>}
        </form>
        <ForgotNote>
          Forgotten password or need access?<br />
          Contact your VuSub account manager or email hello@vusub.ai
        </ForgotNote>
      </Card>
    </Page>
  );
};

export default AgencyLoginPage;
